import { StatementName } from '@/hooks/useCompanyStatementsAPIFormatted/types';
import type { CompanyIndustryHistogramCompanies } from './components/MultipleVsIndustry';
import type { Peer } from './components/MultipleVsPeers/MultipleVsPeers';
import type { PreferredMultiple } from './selector';
import {
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';
import { separateByThreeDigit } from '@/utilities/formatting';
import type { Question } from 'schema-dts';
import { isCurrencyISO } from '@/constants/currencies';

export const generateQandA = (
  question: string,
  answer?: string | null
): Question => ({
  '@type': 'Question',
  name: question,
  acceptedAnswer: {
    '@type': 'Answer',
    text: answer || 'Insufficient data to answer this question',
  },
});

export const formatMultiple = (value: number | null) => {
  if (typeof value !== 'number') return 'n/a';
  if (Math.abs(value) > 9999) return `9,999x`;
  return `${separateByThreeDigit(value, Math.abs(value) > 999 ? 0 : 1)}x`;
};

export const getMultipleFullName = (multiple: PreferredMultiple) =>
  multiple === 'PE'
    ? 'Price to Earnings'
    : multiple === 'PS'
      ? 'Price to Sales'
      : 'Price to Book';

export const getMultipleKey = (multiple: PreferredMultiple) =>
  multiple === 'PB' ? 'pb' : multiple === 'PS' ? 'priceToSales' : 'pe';

export const getDefaultPeer = (): Peer => ({
  companyId: '',
  name: '',
  canonicalURL: '',
  tickerSymbol: '',
  uniqueSymbol: '',
  listingCurrencyISO: 'USD',
  earningsGrowth: 0,
  earningsGrowthFormatted: 'n/a',
  salesGrowth: 0,
  salesGrowthFormatted: 'n/a',
  ratio: 0,
  ratioFormatted: 'n/a',
  marketCap: 0,
  marketCapFormatted: 'n/a',
  marketCapUSD: 0,
  marketCapUSDFormatted: 'n/a',
});

export const mapCompanyToPeer =
  (
    multiple: PreferredMultiple,
    useForward?: boolean,
    preferredMultiple?: PreferredMultiple
  ) =>
  (company: NonNullable<CompanyIndustryHistogramCompanies>[number]): Peer => {
    const {
      canonicalURL,
      tickerSymbol,
      uniqueSymbol,
      name,
      id,
      analysisValue,
      analysisFuture,
      listingCurrencyISO,
    } = company;
    const { marketCap, marketCapUSD } = analysisValue || {};

    const multipleKey = getMultipleKey(multiple);
    const preferredMultipleKey = getMultipleKey(preferredMultiple || multiple);
    const forwardKey =
      multiple === 'PE'
        ? 'forwardPe1Y'
        : multiple === 'PS'
          ? 'forwardPriceToSales1Y'
          : '';

    const preferredCurrentRatio = analysisValue?.[preferredMultipleKey] ?? 0;
    const currentRatio = analysisValue?.[multipleKey] ?? 0;
    const forwardRatio = analysisFuture?.[forwardKey] ?? 0;
    const earningsGrowth = analysisFuture?.netIncomeGrowthAnnual;
    const salesGrowth = analysisFuture?.revenueGrowthAnnual;
    const selectedRatio = useForward ? forwardRatio : currentRatio;
    const ratio = selectedRatio >= 0 ? selectedRatio : null;
    const ratioFormatted = formatterFactor(ratio);

    return {
      companyId: id,
      name: name,
      canonicalURL: canonicalURL,
      tickerSymbol: tickerSymbol,
      uniqueSymbol,
      listingCurrencyISO: isCurrencyISO(listingCurrencyISO)
        ? listingCurrencyISO
        : 'XXX',
      earningsGrowth: earningsGrowth || 0,
      earningsGrowthFormatted: formatterPercentage(earningsGrowth),
      salesGrowth: salesGrowth || 0,
      salesGrowthFormatted: formatterPercentage(salesGrowth),
      ratio,
      ratioFormatted: ratioFormatted === '0x' ? 'n/a' : ratioFormatted,
      marketCap: marketCap || 0,
      marketCapFormatted: formatterCurrencyTruncated(
        marketCap,
        listingCurrencyISO,
        1
      ),
      marketCapUSD: marketCapUSD || 0,
      marketCapUSDFormatted: formatterCurrencyTruncated(marketCapUSD, 'USD'),
      preferredCurrentRatio,
      currentRatio,
      forwardRatio,
    };
  };

export const getLabelWidth = (
  condensed: boolean | undefined,
  lineOneLength: number,
  lineTwoLength: number,
  padding: number,
  letterWidth: number
) => {
  const titleLength = condensed
    ? Math.max(lineOneLength, lineTwoLength)
    : lineOneLength + lineTwoLength;
  const width = 2 * padding + titleLength * letterWidth;
  return width;
};

export const VALUATION_STATEMENTS: StatementName[] = [
  'IsUndervaluedBasedOnDCF',
  'IsHighlyUndervaluedBasedOnDCF',
  'IsGoodValueComparingPreferredMultipleToPeersAverageValue',
  'IsGoodValueComparingPreferredMultipleToIndustry',
  'IsGoodValueComparingRatioToFairRatio',
  'IsAnalystForecastTrustworthy',
];
